import moment from 'moment';
import store from '../../../store/store';
import {
  setAccessToken,
  setAgencyAccessToken,
  setHideWebAuthnPrompt,
  setIsLoggedInViaWebAuthn,
  setIsWebAuthnAvailableInAccount,
  setIsWebAuthnRegistrationSkipped,
} from '../../../store/reducers/authSlice';
import { setIsInitSuccess } from '../../../store/reducers/appSlice';
import { setAppThemeMode, setUserSettings } from '../../../store/reducers/uiSlice';
import { setIsOnboardingMetricsActive } from '../../../store/reducers/userSlice';
import { applyThemeToApp } from '../../helper-functions/themeUtils';
import isNotEmptyString from '../../helper-functions/isNotEmptyString';

/**
 * Apply a login-shaped payload (`{ access_token, user, agency_api_key }`) to
 * Redux WITHOUT navigating. Used by the boot-time session restore
 * (`restore-session.ts`) so a cookie-backed reload re-establishes the SAME
 * in-memory state a fresh login produces — crucially the **seller token**, so
 * the legacy seller-JWT calls (tara/mcp/*, seller-init, dashboard data) keep
 * working after refresh. Mirrors `normalLoginProcess` in
 * `onboardingFlow/login/Login.tsx`, minus navigation.
 *
 * Tokens are written to Redux only (never web storage); the httpOnly cookie
 * remains the durable credential. Returns true when a seller token was applied.
 */
export function applySessionFromResponse(data: any): boolean {
  const dispatch = store.dispatch;
  const access_token = data?.access_token;
  const user = data?.user;
  const agency_api_key = data?.agency_api_key;

  // An INVITED employee has no seller account, so the restore comes back with
  // `agency_api_key` and a null `access_token`. Bailing out here logged those
  // users out on every reload; apply whichever credentials are present instead.
  if (!isNotEmptyString(access_token) && !isNotEmptyString(agency_api_key)) {
    return false;
  }

  dispatch(setIsInitSuccess(false));

  if (isNotEmptyString(access_token)) {
    dispatch(
      setAccessToken({
        sellerToken: access_token,
        type: 'Bearer',
        // The httpOnly cookie (30d) is the real session lifetime; keep the
        // in-memory expiry comfortably ahead so the token-expiry effect doesn't
        // age it out mid-session (every reload re-mints anyway).
        expiresOn: moment().add(21, 'days'),
      })
    );
  }

  if (isNotEmptyString(agency_api_key)) {
    dispatch(setAgencyAccessToken(agency_api_key));
  }

  const hideWebAuthnPrompt = user?.user_settings?.hide_webauthn_prompt;
  dispatch(setIsWebAuthnAvailableInAccount(user?.webauthn ?? false));
  dispatch(setIsLoggedInViaWebAuthn(false));
  dispatch(setHideWebAuthnPrompt(hideWebAuthnPrompt ?? 0));
  if (hideWebAuthnPrompt === 1) {
    dispatch(setIsWebAuthnRegistrationSkipped(true));
  }

  if (user?.user_settings) {
    dispatch(setUserSettings(user?.user_settings));

    const isDarkMode = store.getState().UI?.theme?.dark;
    applyThemeToApp(user?.user_settings?.theme_mode as any, isDarkMode, () =>
      dispatch(setAppThemeMode())
    );
  }

  dispatch(
    setIsOnboardingMetricsActive(user?.is_onboarding_metrics_active === true)
  );

  // Whether a SELLER token was applied — callers use this to decide if the
  // legacy seller-JWT surfaces (seller-init, tara/mcp/*) can be called. False
  // for an invited employee, whose session is agency-only.
  return isNotEmptyString(access_token);
}
